import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, MessageCircle } from "lucide-react";
import axios from "axios";
import { BRAND, TREATMENTS, UI } from "@/content";
import { useLang } from "@/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ eyebrow, title, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="text-center max-w-2xl mx-auto mb-14"
    >
      <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-goldbright flex items-center justify-center gap-3 mb-5">
        <span className="h-px w-9 bg-gold/70 inline-block" />
        {eyebrow}
        <span className="h-px w-9 bg-gold/70 inline-block" />
      </p>
      <h2 className="font-serif font-medium text-4xl sm:text-5xl text-cream leading-[1.08]">
        {title}
      </h2>
      <p className="mt-5 text-sage font-light leading-relaxed">{sub}</p>
    </motion.div>
  );
}

export default function Services() {
  const { lang } = useLang();
  const t = UI[lang] || UI.es;
  const [items, setItems] = useState(TREATMENTS);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => {
        const s = r.data?.services;
        if (Array.isArray(s) && s.length) {
          const bundled = TREATMENTS.reduce((m, x) => ({ ...m, [x.id]: x }), {});
          setItems(
            s.map((x) => {
              const b = bundled[x.id] || {};
              return {
                id: x.id,
                image: x.image || b.image || bundled["masaje-relajante-1h"].image,
                duration: x.duration ?? b.duration ?? "—",
                price: x.price,
                tag: x.tag || b.tag || { es: "En Fresha", en: "On Fresha" },
                title: x.title || b.title || { es: x.name || x.id, en: x.name || x.id },
                desc: x.desc || b.desc || {
                  es: "Tratamiento disponible en nuestra carta de Fresha. Reserva online al instante.",
                  en: "Treatment available on our Fresha menu. Book online instantly.",
                },
              };
            })
          );
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="tratamientos"
      className="relative py-24 lg:py-28 scroll-mt-16"
      data-testid="services-section"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader eyebrow={t.services.eyebrow} title={t.services.title} sub={t.services.sub} />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <motion.article
              key={item.id}
              data-testid={`treatment-card-${item.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group bg-moss rounded-2xl border border-gold/15 overflow-hidden flex flex-col hover:border-gold/50 hover:shadow-[0_24px_50px_rgba(0,0,0,0.4)] hover:-translate-y-1 transition-all duration-500"
            >
              <div className="relative overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title[lang]}
                  loading="lazy"
                  className="w-full h-52 object-cover [transition-duration:1200ms] ease-out group-hover:scale-[1.06]"
                />
                <span className="absolute top-4 left-4 font-mono text-[9px] tracking-[0.2em] uppercase bg-forest/85 backdrop-blur text-goldbright rounded-full px-3 py-1.5 border border-gold/30">
                  {item.tag[lang]}
                </span>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-serif text-3xl text-goldbright">€{item.price}</span>
                  <span className="font-mono text-[11px] tracking-wider text-sage flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-gold" />
                    {item.duration}
                  </span>
                </div>
                <h3 className="mt-3 font-serif text-2xl text-cream leading-snug">
                  {item.title[lang]}
                </h3>
                <p className="mt-2.5 text-sm text-sage font-light leading-relaxed flex-1">
                  {item.desc[lang]}
                </p>
                <a
                  href={BRAND.fresha}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`treatment-book-${item.id}`}
                  className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-gold border-t border-gold/15 pt-4 hover:text-goldbright transition-colors duration-300 group/link"
                >
                  {t.services.book}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={BRAND.fresha}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-book-all-fresha"
            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-[15px] font-medium text-forest hover:bg-goldbright hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
          >
            {t.services.bookAll}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-book-whatsapp"
            className="inline-flex items-center gap-2.5 rounded-full border border-wamist/50 text-wamist px-7 py-4 text-[15px] font-medium hover:bg-wamist/10 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
          >
            <MessageCircle className="w-[18px] h-[18px]" />
            {t.services.waBook}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
